import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Select, MenuItem, FormControl, Stack
} from '@mui/material';
import * as api from './pricelistsApi';
import type { Pricelist, Currency, SalesChannel, CustomerGroup, PsychologicalPrice } from './pricelistsApi';
import { useDetailView } from '../common/useDetailView';
import { sx13, FormRow, DetailViewShell } from '../common/detailView';

interface Props {
    pricelistId: number | null;
}

export function PricelistView({ pricelistId }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<Pricelist>();

    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [salesChannels, setSalesChannels] = useState<SalesChannel[]>([]);
    const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
    const [psychologicalPrices, setPsychologicalPrices] = useState<PsychologicalPrice[]>([]);

    const isNew = pricelistId == null;

    const loadData = useCallback(async () => {
        await detail.withLoading(async () => {
            const [currenciesData, salesChannelsData, customerGroupsData, psychPricesData] = await Promise.all([
                api.getCurrencies(),
                api.getSalesChannels(),
                api.getCustomerGroups(),
                api.getPsychologicalPrices(),
            ]);

            setCurrencies(currenciesData);
            setSalesChannels(salesChannelsData);
            setCustomerGroups(customerGroupsData);
            setPsychologicalPrices([{ id: 0, name: '-' }, ...psychPricesData]);

            if (isNew) {
                const defaultCurrency = currenciesData.find(c => c.code === 'SEK') ?? currenciesData[0] ?? null;
                detail.setItem({
                    id: 0,
                    pricelistCode: '',
                    pricelistName: '',
                    currency: defaultCurrency,
                    salesChannel: salesChannelsData[0] ?? null,
                    customerGroup: null,
                    priceFactor: 1,
                    exchangeRate: defaultCurrency?.currencyRate ?? 1,
                    leastNetProfitPercentage: 0,
                    psychologicalPrice: { id: 0, name: '-' },
                });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getPricelistBrief(pricelistId as number);
                if (!data) throw new Error(`Pricelist ${pricelistId} returned empty response`);
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [pricelistId, isNew]);

    useEffect(() => { loadData(); }, [loadData]);

    const pricelist = detail.item;

    // Validation
    const isPricelistCodeValid = (): boolean =>
        !!pricelist?.pricelistCode && pricelist.pricelistCode.length > 0;

    const isPricelistNameValid = (): boolean =>
        !!pricelist?.pricelistName && pricelist.pricelistName.length > 0;

    const hasChosenCurrency = (): boolean =>
        !!pricelist?.currency && pricelist.currency.id > 0;

    const isSalesChannelValid = (): boolean =>
        !!pricelist?.salesChannel && pricelist.salesChannel.salesChannelCode.length > 0;

    const isAllValid = (): boolean =>
        isPricelistCodeValid() && isPricelistNameValid() && hasChosenCurrency();

    const onCurrencyChange = (currencyId: number | string) => {
        const c = currencies.find(cur => cur.id === currencyId);
        if (c) {
            detail.update('currency', c);
            detail.update('exchangeRate', c.currencyRate);
        }
    };

    const save = async () => {
        if (!pricelist) return;
        await detail.withSaving(async () => {
            if (detail.isNew) {
                const response = await api.createPricelist(pricelist);
                detail.setItem(response);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            } else {
                const response = await api.updatePricelist(pricelist);
                detail.setItem(response);
                detail.setIsEditing(false);
            }
        });
    };

    const currencyCode = pricelist?.currency?.code ?? '';
    const priceFactor = pricelist?.priceFactor ?? 1;
    const exchangeRate = pricelist?.exchangeRate ?? 1;

    return (
        <DetailViewShell<Pricelist>
            featureKey="devFeature_reactPricelistView"
            title={`${pricelist?.pricelistCode ?? ''} ${pricelist?.pricelistName ?? ''}`}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={isAllValid()}
            loading={detail.loading}
            error={detail.error}
            item={pricelist}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onClearError={() => detail.setError(null)}
        >
            {(item) => (
                <Stack spacing={2}>
                    {/* Code */}
                    <FormRow label={t('pricelist.pricelistCode')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{item.pricelistCode}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={item.pricelistCode}
                                onChange={e => detail.update('pricelistCode', e.target.value)}
                                error={!isPricelistCodeValid()}
                            />
                        )}
                    </FormRow>

                    {/* Name */}
                    <FormRow label={t('pricelist.pricelistName')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{item.pricelistName}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={item.pricelistName}
                                onChange={e => detail.update('pricelistName', e.target.value)}
                                error={!isPricelistNameValid()}
                            />
                        )}
                    </FormRow>

                    {/* Currency */}
                    <FormRow label={t('pricelist.currency')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{item.currency?.code}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth error={!hasChosenCurrency()}>
                                <Select
                                    value={item.currency?.id ?? ''}
                                    onChange={e => onCurrencyChange(e.target.value)}
                                >
                                    {[...currencies]
                                        .sort((a, b) => a.code.localeCompare(b.code))
                                        .map(c => (
                                            <MenuItem key={c.id} value={c.id}>{c.code}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Sales Channel */}
                    <FormRow label={t('pricelist.salesChannel')}>
                        {!detail.isNew ? (
                            <Typography sx={sx13}>
                                {item.salesChannel?.salesChannelCode} {item.salesChannel?.salesChannelDescription}
                            </Typography>
                        ) : (
                            <FormControl size="small" fullWidth error={!isSalesChannelValid()}>
                                <Select
                                    value={item.salesChannel?.id ?? ''}
                                    onChange={e => {
                                        const sc = salesChannels.find(s => s.id === e.target.value);
                                        if (sc) detail.update('salesChannel', sc);
                                    }}
                                >
                                    {[...salesChannels]
                                        .sort((a, b) => a.salesChannelCode.localeCompare(b.salesChannelCode))
                                        .map(sc => (
                                            <MenuItem key={sc.id} value={sc.id}>
                                                {sc.salesChannelCode} {sc.salesChannelDescription}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Customer Group */}
                    <FormRow label={t('pricelist.customerGroup')}>
                        {!detail.isNew ? (
                            <Typography sx={sx13}>
                                {item.customerGroup?.code} {item.customerGroup?.name}
                            </Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={item.customerGroup?.id ?? ''}
                                    onChange={e => {
                                        const cg = customerGroups.find(g => g.id === e.target.value);
                                        detail.update('customerGroup', cg ?? null);
                                    }}
                                >
                                    <MenuItem value=""><em>&nbsp;</em></MenuItem>
                                    {[...customerGroups]
                                        .sort((a, b) => a.name.localeCompare(b.name))
                                        .map(cg => (
                                            <MenuItem key={cg.id} value={cg.id}>{cg.code} {cg.name}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Price Factor */}
                    <FormRow label={t('pricelist.priceFactor')}>
                        {!detail.isEditing ? (
                            <Box>
                                <Typography sx={sx13}>{priceFactor * 100}%</Typography>
                                <Typography sx={{ ...sx13, fontStyle: 'italic', mb: '5px' }}>
                                    {t('common.example')}: 299 {currencyCode} ({t('article.originalprice')}) → {(299 * priceFactor).toFixed(2)} {currencyCode} ({t('article.priceOnPricelist')})
                                </Typography>
                            </Box>
                        ) : (
                            <Box>
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ step: 0.01 }}
                                    value={item.priceFactor}
                                    onChange={e => detail.update('priceFactor', e.target.value === '' ? 0 : Number(e.target.value))}
                                />
                                <Typography sx={{ ...sx13, fontStyle: 'italic', mb: '5px' }}>
                                    {t('common.example')}: 299 {currencyCode} ({t('article.originalprice')}) → {(299 * priceFactor).toFixed(2)} {currencyCode} ({t('article.priceOnPricelist')})
                                </Typography>
                            </Box>
                        )}
                    </FormRow>

                    {/* Exchange Rate */}
                    <FormRow label={t('pricelist.exchangeRate')}>
                        {!detail.isEditing ? (
                            <Box>
                                <Typography sx={sx13}>{exchangeRate}</Typography>
                                <Typography sx={{ ...sx13, fontStyle: 'italic', mb: '5px' }}>
                                    {t('common.example')}: 299 SEK → {(299 / exchangeRate).toFixed(2)} {currencyCode}, 299 {currencyCode} → {(299 * exchangeRate).toFixed(2)} SEK
                                </Typography>
                            </Box>
                        ) : (
                            <Box>
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ step: 0.01 }}
                                    value={item.exchangeRate}
                                    onChange={e => detail.update('exchangeRate', e.target.value === '' ? 0 : Number(e.target.value))}
                                />
                                <Typography sx={{ ...sx13, fontStyle: 'italic', mb: '5px' }}>
                                    {t('common.example')}: 299 SEK → {(299 / exchangeRate).toFixed(2)} {currencyCode}, 299 {currencyCode} → {(299 * exchangeRate).toFixed(2)} SEK
                                </Typography>
                            </Box>
                        )}
                    </FormRow>

                    {/* Psychological Price */}
                    <FormRow label={t('pricelist.psychologicalPrice')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{item.psychologicalPrice?.name}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={item.psychologicalPrice?.id ?? 0}
                                    onChange={e => {
                                        const pp = psychologicalPrices.find(p => p.id === e.target.value);
                                        detail.update('psychologicalPrice', pp ?? null);
                                    }}
                                >
                                    {psychologicalPrices.map(pp => (
                                        <MenuItem key={pp.id} value={pp.id}>{pp.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>
                </Stack>
            )}
        </DetailViewShell>
    );
}
